import {ConversationStageType} from '@frontapp/insights/conversation_stages';
import React, {FC} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Localizer} from '../../../../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {RendererOf} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {UserTheme} from '../../../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  activityConversationStageUpdateColors,
  activityConversationStageUpdateLabels,
  activityConversationStageUpdateTooltipContent,
} from '../../../../../../srcCommon/helpers/models/activityConversationStageUpdateHelpers';
import {conversationStagesHelpCenterUrl} from '../../../../../analytics/helpers/reportHelpers/analyticsReportHelpers';
import {ActivityConversationStageUpdatePill} from './activityConversationStageUpdatePill';
import {ActivityConversationStageUpdateTooltip} from './activityConversationStageUpdateTooltip';

/*
 * Props.
 */

interface ActivityConversationStageUpdatePopoverProps {
  conversationStageType: ConversationStageType;
  showBottomChevron?: boolean;
  hasNoBorder?: boolean;
  isTooltipDisabled?: boolean;
  isModalDisabled?: boolean;
  modalRenderer?: RendererOf<PopoverTransferProps>;
  size?: VisualSizesEnum;
}

/*
 * Style.
 */

const StyledActivityConversationStageUpdatePopoverWrapperDiv = styled.div`
  display: inline-grid;
`;

/*
 * Component.
 */

export const ActivityConversationStageUpdatePopover: FC<ActivityConversationStageUpdatePopoverProps> = (
  props,
) => {
  const localize = useLocalize();
  const theme = useUserTheme();
  return (
    <StyledActivityConversationStageUpdatePopoverWrapperDiv>
      <PopoverCoordinator
        isDisabled={!props.modalRenderer}
        renderButton={(isModalOpen) =>
          renderPill(
            isModalOpen,
            localize,
            theme,
            conversationStagesHelpCenterUrl,
            props.conversationStageType,
            props.hasNoBorder,
            props.isTooltipDisabled,
            props.showBottomChevron,
            props.size,
          )
        }
        renderModal={props.modalRenderer ?? (() => null)}
      />
    </StyledActivityConversationStageUpdatePopoverWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderPill(
  isModalOpen: boolean,
  localize: Localizer,
  theme: UserTheme,
  learnMoreUrl: string,
  conversationStageType: ConversationStageType,
  hasNoBorder = false,
  isTooltipDisabled = false,
  showBottomChevron = false,
  size = VisualSizesEnum.MEDIUM,
) {
  const conversationStageTypeLabel = activityConversationStageUpdateLabels[conversationStageType];
  const tooltipContent = activityConversationStageUpdateTooltipContent[conversationStageType](localize);
  const {color, borderColor, backgroundColor, hoverColor} =
    activityConversationStageUpdateColors[conversationStageType](theme);

  return (
    <ActivityConversationStageUpdateTooltip
      isModalOpen={isModalOpen}
      tooltipContent={tooltipContent}
      conversationStageTypeLabel={conversationStageTypeLabel}
      placement="bottom"
      learnMoreUrl={learnMoreUrl}
      isDisabled={isTooltipDisabled || showBottomChevron}
    >
      <ActivityConversationStageUpdatePill
        conversationStageTypeLabel={conversationStageTypeLabel}
        color={color}
        borderColor={hasNoBorder ? backgroundColor : borderColor}
        backgroundColor={isModalOpen ? hoverColor : backgroundColor}
        hoverColor={isTooltipDisabled ? backgroundColor : hoverColor}
        showBottomChevron={showBottomChevron}
        size={size}
      />
    </ActivityConversationStageUpdateTooltip>
  );
}
